<template>
  <div>
    <FormCard
      v-model="customerData"
      @formSubmit="$emit('formSubmit', $event)">
      <CardForm
        :resource="customerData"
        resource-name="customer">
        <RadioControlWrapper
          label="Customer Type">
          <BoolRadioControl
            v-model="customerData.account_type"
            name="customer[account_type]"
            label="Individual"
            default-checked
            used-value="Individual" />
          <BoolRadioControl
            v-model="customerData.account_type"
            name="customer[account_type]"
            label="Business"
            used-value="Business" />
        </RadioControlWrapper>
        <CardDivider />
        <div class="form-card-group form-card-group-nowrap">
          <div class="form-card-group-item form-card-group-item-small">
            <ItemSelect
              v-model="customerData.salutation"
              :items="salutationOptions"
              name="customer[salutation]"
              key-method="name"
              value-method="name"
              :object-method="false" />
          </div>
          <div class="form-card-group-item form-card-group-item-medium">
            <TextInput
              v-model="customerData.first_name"
              name="customer[first_name]"
              width="medium"
              required />
          </div>
          <div class="form-card-group-item form-card-group-item-medium">
            <TextInput
              v-model="customerData.last_name"
              name="customer[last_name]"
              width="medium"
              required />
          </div>
        </div>
        <TextInput
          v-model="customerData.company_name"
          name="customer[company_name]"
          width="medium" />
        <TextInput
          v-model="displayName"
          name="customer[display_name]"
          disclaimer="This is the name that will be shown on invoices, expenses created for this customer."
          width="medium"
          required />
        <CardDivider />
        <TextInput
          v-model="customerData.email"
          name="customer[email]"
          width="medium" />
        <div class="form-card-group">
          <div class="form-card-group-item col-24 col-md-12">
            <label for="mobileInput" class="custom-form-group-label">Mobile Number</label>
            <vue-tel-input
              v-model="customerData.mobile_number"
              input-id="mobileInput"
              ref="mobileInput"
              name="customer[mobile_number]"
              :ignored-countries="['IL']"
              :enabled-country-code="true"
              :preferred-countries="['LB', 'SA']"
              :valid-characters-only="true"
              :default-country="'LB'"
              mode="national"
              :disabled-fetching-country="true"
              wrapper-classes="ihjoz-tel-input"
              input-classes="ihjoz-tel-input-field ihjoz-tel-input-field-code-visible"
              @onInput="onInputMobileNumber"
              @input="validateMobileNumber" />
            <input
              :value="selectedPhoneCountryCode"
              name="customer[mobile_country_code]"
              type="hidden">
          </div>
          <div class="form-card-group-item col-24 col-md-12">
            <label for="workInput" class="custom-form-group-label">Work Number</label>
            <vue-tel-input
              v-model="customerData.work_number"
              input-id="workInput"
              ref="workInput"
              name="customer[work_number]"
              :ignored-countries="['IL']"
              :enabled-country-code="true"
              :enabled-country-codes="['LB', 'SA']"
              :preferred-countries="['LB', 'SA']"
              :valid-characters-only="true"
              :default-country="'LB'"
              mode="national"
              :disabled-fetching-country="true"
              wrapper-classes="ihjoz-tel-input"
              input-classes="ihjoz-tel-input-field ihjoz-tel-input-field-code-visible"
              @onInput="onInputWorkNumber"
              @input="validateWorkNumber" />
            <input
              :value="selectedWorkCountryCode"
              name="customer[work_country_code]"
              type="hidden">
          </div>
        </div>
        <CardDivider />
        <TextInput
          v-model="customerData.reference_id"
          name="customer[reference_id]"
          disclaimer="Add any customer reference number for tracking purposes."
          custom-label="Customer Reference Number"
          width="medium" />
        <CardDivider />
        <div class="form-card-group">
          <div class="form-card-group-item col-24 col-md-8">
            <TextInput
              v-model="customerData.tax_type"
              custom-label="Tax Reference Name"
              placeholder="eg: VAT#, MOF#..."
              name="customer[tax_type]" />
          </div>
          <div class="form-card-group-item col-24 col-md-8">
            <TextInput
              v-model="customerData.tax_number"
              placeholder="123456-601"
              name="customer[tax_number]" />
          </div>
        </div>
        <CardDivider />
        <div class="form-card-group">
          <div class="form-card-group-item form-card-group-item-vertical col-24 col-md-12">
            <div class="form-card-group-item-title">
              <h3 class="form-card-group-item-title-text">
                Billing Address
              </h3>
            </div>
            <MultilineTextInput
              v-model="customerBillingAddress.address"
              name="customer[billing_address][address]"
              custom-label="Address" />
            <ItemSelect
              v-model="customerBillingAddress.country"
              :items="publicCountries"
              name="customer[billing_address][country]"
              label="Country"
              value-method="name"
              :shortened="false" />
            <TextInput
              v-model="customerBillingAddress.city"
              name="customer[billing_address][city]"
              custom-label="City" />
            <TextInput
              v-model="customerBillingAddress.zip_code"
              name="customer[billing_address][zip_code]"
              custom-label="Zip Code" />
            <TextInput
              v-model="customerBillingAddress.attention"
              name="customer[billing_address][attention]"
              custom-label="Attention" />
            <div class="form-group custom-form-group">
              <label for="billingMobileInput" class="custom-form-group-label">Phone Number</label>
              <vue-tel-input
                ref="billingMobileInput"
                v-model="customerBillingAddress.phone_number"
                input-id="billingMobileInput"
                name="customer[billing_address][phone_number]"
                :ignored-countries="['IL']"
                :enabled-country-code="true"
                :preferred-countries="['LB', 'SA']"
                :valid-characters-only="true"
                :default-country="'LB'"
                mode="national"
                :disabled-fetching-country="true"
                wrapper-classes="ihjoz-tel-input"
                input-classes="ihjoz-tel-input-field ihjoz-tel-input-field-code-visible"
                @onInput="onInputBillingNumber"
                @input="validateBillingMobileNumber" />
              <input
                :value="selectedBillingCountryCode"
                name="customer[billing_address][billing_country_code]"
                type="hidden">
            </div>
          </div>

          <div class="form-card-group-item form-card-group-item-vertical col-24 col-md-12">
            <div class="form-card-group-item-title">
              <h3 class="form-card-group-item-title-text">
                Shipping Address
              </h3>
              <a id="same-as-billing" href="#" class="form-card-group-item-title-action" @click="autoFillShippingAddress($event)">
                Same as billing
              </a>
            </div>
            <MultilineTextInput
              v-model="customerShippingAddress.address"
              name="customer[shipping_address][address]"
              custom-label="Address" />
            <ItemSelect
              v-model="customerShippingAddress.country"
              :items="publicCountries"
              name="customer[shipping_address][country]"
              label="Country"
              value-method="name"
              :shortened="false" />
            <TextInput
              v-model="customerShippingAddress.city"
              name="customer[shipping_address][city]"
              custom-label="City" />
            <TextInput
              v-model="customerShippingAddress.zip_code"
              name="customer[shipping_address][zip_code]"
              custom-label="Zip Code" />
            <TextInput
              v-model="customerShippingAddress.attention"
              name="customer[shipping_address][attention]"
              custom-label="Attention" />
            <div class="form-group custom-form-group">
              <label for="shippingMobileInput" class="custom-form-group-label">Phone Number</label>
              <vue-tel-input
                ref="shippingMobileInput"
                v-model="customerShippingAddress.phone_number"
                input-id="shippingMobileInput"
                name="customer[shipping_address][phone_number]"
                :ignored-countries="['IL']"
                :enabled-country-code="true"
                :preferred-countries="['LB', 'SA']"
                :valid-characters-only="true"
                :default-country="'LB'"
                mode="national"
                :disabled-fetching-country="true"
                wrapper-classes="ihjoz-tel-input"
                input-classes="ihjoz-tel-input-field ihjoz-tel-input-field-code-visible"
                @onInput="onInputShippingNumber"
                @input="validateShippingMobileNumber" />
              <input
                :value="selectedShippingCountryCode"
                :name="`customer[shipping_address][shipping_country_code]`"
                type="hidden">
            </div>
          </div>
        </div>
      </CardForm>
    </FormCard>
  </div>
</template>

<script>
import FormCard from "../../../shared/components/UI/FormCard.vue";
import CardForm from "../../../shared/components/UI/CardForm.vue";
import ItemSelect from "../../../shared/components/UI/ItemSelect.vue";
import TextInput from "../../../shared/components/UI/TextInput.vue";
import MultilineTextInput from "../../../shared/components/UI/MultilineTextInput.vue";
import RadioControlWrapper from "../../../shared/components/UI/RadioControlWrapper.vue";
import BoolRadioControl from "../../../shared/components/UI/BoolRadioControl.vue";
import CardDivider from "../../../shared/components/UI/CardDivider.vue";
import { VueTelInput } from "vue-tel-input";
import CustomerFormMixin from "./CustomerFormMixin";

export default {
  name: "CustomerForm",

  components: {
    FormCard,
    CardForm,
    ItemSelect,
    TextInput,
    MultilineTextInput,
    RadioControlWrapper,
    BoolRadioControl,
    VueTelInput,
    CardDivider
  },

  mixins: [CustomerFormMixin],

  props: {
    customer: { type: Object }
  },

  data () {
    return {
      customerData: this.customer
    };
  },

  computed: {
    submitButtonName () {
      return this.customerData.id ? "Update" : "Create";
    },

    formTitle () {
      return this.customerData.id ? "Edit Customer" : "New Customer";
    }
  }
};
</script>
